import { EVENT } from "@/constants/event";
import {
  AUTH_TYPE,
  MATERIAL_SOURCE_TYPE,
  MATERIAL_VIP_VERSION,
} from "@/constants/gaodingVip";
import {
  AUTH_RIGHT_PACKAGE_CODES,
  isBigCompanyByUser,
  isCompanyVipByUser,
  isNoAuthByUser,
} from "@/constants/vip";
import useAppStore from "@/context/store";
import reporter from "@/tracker/report/reporter";
import { getPage } from "@/tracker/shared/global";
import { getTypeNum, handleDownloadFileName } from "@/utils/download";
import { isGdMerged } from "@/utils/gaoding";
import {
  isPersonalOrg,
  isPlusByGdVip,
  matchDownloadRight,
  NEW_VIP_RIGHT_CODE,
} from "@/utils/gaodingVip";
import { OrgService } from "@/utils/gd-org";
import { gdSlsReportError } from "@/utils/gdSlsRum";
import getTimeDuration from "@/utils/getTimeDuration";
import { gaodingEditHost, VIP_CERT_PAGE } from "@/utils/host";
import imageUrl from "@/utils/imageUrl";
import { isThirdPartyMaterial } from "@/utils/materialUtils";
import useReq from "@/utils/useReq";
import { useMemoizedFn } from "ahooks";
import { Button, Checkbox } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { get, maxBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  getBizDownloadList,
  getDownloadInfo,
  getMySpaceId,
  getPreDownloadInfo,
  materialDownloadSaveToSpace,
} from "../../services";
import { REQUEST_MESSAGE_KEY } from "../../utils/request";
import HBIcon from "../HBIcon";
import HBMessage from "../HBMessage";
import HBModal, { ModalProps } from "../HBModal";
import ModalWrap from "../HBModal/ModalWrap";
import Image from "../Image";
import { AdditionGoodDialog, BuyVipDialog } from "../Vip";
import styles from "./index.module.less";
import { SubjectSelect } from "./SubjectSelect";

const getFormatTip = (format) => {
  switch (format) {
    case "PSD":
      return "（建议使用Adobe Photoshop软件打开）";
    case "C4D":
      return "（建议使用CINEMA 4D软件打开）";
    case "EPS":
    case "AI":
      return "（建议使用Adobe Illustrator软件打开）";
    case "CDR":
      return "（建议使用CorelDRAWru软件打开）";
    case "ZIP":
    case "RAR":
      return "（建议您先解压，再根据素材格式用相应软件打开）";
    default:
      return "";
  }
};

interface MaterialDownloadInfoModalProps extends ModalProps {
  pin: IPin;
  info: PreDownLoadRes;
  searchWord?: string;
  editOnline?: boolean;
  file: {
    file_format: string;
    file_size: string;
    file_type: string;
  };
  gdVip: IGdOrgVip["vip"];
  userRights: IUserRight[];
  subjects: CERTSubject[];
  defaultSelectSubject: number[];
  onDownload?: () => void;
  onOk?: () => void;
  isSaveToBoard?: boolean;
  recordId?: string;
}

function MaterialDownloadInfoModal({
  pin,
  info,
  file,
  userRights,
  subjects,
  defaultSelectSubject = [],
  searchWord,
  editOnline,
  gdVip,
  onDownload,
  onOk,
  isSaveToBoard,
  recordId,
  ...props
}: MaterialDownloadInfoModalProps & ModalProps) {
  const materialInfo = pin.material_info;
  const fileMaterial = pin.file_material;
  const format = file?.file_format?.toUpperCase() || "";
  const user = useAppStore((s) => s.user);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [preDownloadInfo, setPreDownloadInfo] = useState<PreDownLoadRes>(info);
  // 是否保存到创作空间
  const [saveToSpace, setSaveToSpace] = useState(true);
  const { runAsync: runDownload, loading } = useReq(getDownloadInfo, {
    manual: true,
  });

  // 获取最晚过期的一个会员信息
  const lastVip = useMemo(() => {
    return maxBy(gdVip, (item) => new Date(item.expires_at).valueOf());
  }, [gdVip]);

  const vipLeftDay = useMemo(() => {
    return dayjs(lastVip.expires_at).diff(dayjs(), "day", true);
  }, [lastVip?.expires_at]);

  const orgInfo = useAppStore((s) => s.orgInfo);

  const subjectSelectOption = useMemo(() => {
    if (!subjects) {
      return [];
    }

    return subjects
      .filter((item) => item.subject_info?.name)
      .map((item) => ({
        label: item.subject_info.name,
        value: item.id,
      }));
  }, [subjects]);

  const [subjectValue, setSubjectValue] = useState(defaultSelectSubject);

  const { run: preDownload } = useReq(
    async (vip_cert_subject_ids?: number[]) => {
      const result = await getPreDownloadInfo({
        material_id: pin.material_info?.id,
        download_type: 1,
        type: getTypeNum(pin?.file_material?.material_category),
        ...(vip_cert_subject_ids ? { vip_cert_subject_ids } : null),
      });

      if (result.err) {
        HBMessage.error("当前授权主体无法下载");
        return;
      }

      setPreDownloadInfo(result);
    },
    { manual: true }
  );

  const rightData = matchDownloadRight({
    preDownloadInfo,
    materialInfo,
  });

  const isNewVip = rightData?.code !== NEW_VIP_RIGHT_CODE.OLD_MATERIAL_DOWNLOAD;

  // 第三方素材类型, 只有是新会员时才展示
  const materialType = useMemo(() => {
    if (isNewVip) {
      if (isThirdPartyMaterial(materialInfo)) {
        // 平面模板、高端免抠，视频模板 展示为 第三方平面视频
        // 图片，视频 展示为 第三方图片视频
        if (
          fileMaterial.material_category === "平面模板" ||
          fileMaterial.material_category === "高端免抠" ||
          fileMaterial.material_category === "视频模板"
        ) {
          return "第三方平面视频";
        } else if (
          fileMaterial.material_category === "图片" ||
          fileMaterial.material_category === "视频"
        ) {
          return "第三方图片视频";
        }
      } else {
        return "官方素材";
      }
    }
  }, [fileMaterial, isNewVip, materialInfo]);

  // 根据素材下载记录保存到创作空间
  const saveToSpaceByDownloadList = useMemoizedFn(async () => {
    if (!saveToSpace) return;
    let result = null;
    if (!recordId) {
      result = await getBizDownloadList({
        material_id: pin.material_info?.id || 0,
        vip_cert_subject_ids: subjectValue ? subjectValue?.join(",") : "",
        page_num: 1,
        page_size:
          !subjectValue || subjectValue?.length === 0
            ? 1
            : subjectValue?.length,
      });
    } else {
      result = {
        list: [{ id: recordId }],
      };
    }
    // 获取创作空间ID
    const spaceId = await getMySpaceId();
    const repositoryId = get(spaceId, "[0].repository_id", 0);
    if (!repositoryId) return;
    if (!Array.isArray(result.list) || result.list.length === 0) return;
    // 保存到库
    const data = await materialDownloadSaveToSpace({
      download_ids: result.list.map((v) => v.id),
      folder_id: 0,
      repository_id: repositoryId,
    });
    const isSuccess = !!get(data, "id", 0);
    if (isSuccess) {
      HBMessage.success({
        content: (
          <>
            已保存到“创作空间”
            <a href={`https://${gaodingEditHost}/dam/my`} target="_blank">
              &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;立即前往
            </a>
          </>
        ),
        duration: 5,
      });
    }
    return isSuccess;
  });

  const saveToSpaceOnly = useMemoizedFn(async () => {
    setSaveToSpace(true);
    // 保存到创作空间
    const isSuccess = await saveToSpaceByDownloadList();
    isSuccess && onOk && onOk();
  });

  const onDownloadClick = useMemoizedFn(async () => {
    try {
      const materialInfo = pin.material_info;
      const materialId = materialInfo.id;

      if (loading) {
        return;
      }

      let format = "";

      if (pin?.file_material?.material_category === "视频") {
        format = "HD";
      } else {
        format = file?.file_type;
      }

      setDownloadLoading(true);
      const result = await runDownload({
        material_id: materialId,
        type: getTypeNum(pin?.file_material?.material_category), // 素材类型
        download_type: 1, //会员身份下载
        // system_code: preDownloadInfo?.vips[0].system_code,
        pin_id: pin?.pin_id,
        filename: handleDownloadFileName(pin, file),
        ...(file?.file_format ? { material_type: file.file_format } : null),
        ...(format ? { format } : null),
        // 授权书ID
        ...(subjectValue ? { vip_cert_subject_ids: subjectValue } : null),
      });
      saveToSpaceByDownloadList();
      useAppStore.setState({
        downloadLeft: Number(rightData?.inspect?.left) - downloadUsage,
      });

      // 同步存在没有上报的情况，故异步上报
      setTimeout(() => {
        // 埋点上报
        try {
          const pageName = getPage();
          // 保存到创作空间
          if (isSaveToBoard) {
            reporter({
              event_id: 39008,
              subject_name: "弹窗",
              page_name: "素材下载记录弹窗",
              button_name: "确认保存",
              module_name: "版权素材保存到弹窗",
              ...pageName,
            });
            return;
          }
          // 下载到本地
          const trackData = {
            event_id: 39008,
            event_name: "button_click",
            button_name: saveToSpace
              ? "确认下载_保存到空间"
              : "确认下载_不保存到空间",
            subject_name: "弹窗",
            module_name: "版权素材保存到弹窗",
            search_word: searchWord,
            ...pageName,
          };

          reporter(trackData);
        } catch (e) {
          // do nothing
        }
      }, 0);

      setDownloadLoading(false);

      if (result.code === 1) {
        if (editOnline && result.url) {
          // 如果是去编辑的话
          window.open(
            `https://${gaodingEditHost}/editor/design?mode=open_remote_file&resource_url=${encodeURIComponent(
              result.url
            )}&action_extension=autofocus`
          );
        } else {
          const eventData = {
            url: result.url,
            detail: result,
            pin: pin,
          };
          window.dispatchEvent(
            // @ts-ignore
            new CustomEvent(EVENT.MATERIAL_DOWNLOAD, { detail: eventData })
          );
        }

        onOk && onOk();
      } else {
        console.warn("下载异常", result);

        let errMsg = result.message || result.msg || result?.data?.message;
        if (errMsg === "视觉中国请求异常！") {
          errMsg = "素材异常，暂时无法下载";
        }

        HBMessage.error({
          content: errMsg || "下载异常",
          key: REQUEST_MESSAGE_KEY,
          duration: 3,
        });

        gdSlsReportError("下载失败：" + (errMsg || "下载异常"));
      }
    } catch (error) {
      setDownloadLoading(false);
      throw error;
    }
  });

  const onIncreaseDownloadCount = useCallback(() => {
    // @ts-ignore
    props.onCancel && props.onCancel();

    AdditionGoodDialog.open({
      onOk: onDownload,
      source_location: "增加下载次数",
      // 第三方素材下载，自产传："OFFICIAL_ MATERIAL_DOWNLOAD"
      // 第三方传："THIRD_PARTY_MATERIAL_GRAPHIC"
      ...(isNewVip
        ? {
            materialSourceType: isThirdPartyMaterial(materialInfo)
              ? MATERIAL_SOURCE_TYPE.THIRD
              : MATERIAL_SOURCE_TYPE.OFFICIAL,
          }
        : {}),
    });
  }, [onDownload, props]);

  const openBuyVipDialog = useCallback(
    (source_location?: string) => {
      // @ts-ignore
      props.onCancel && props.onCancel();
      BuyVipDialog.open({
        source_location,
      });
    },
    [props]
  );

  const getAuthText = useMemoizedFn((noAuth) => {
    if (noAuth) {
      return (
        <>
          仅供交流学习，<span className="color-primary">不可商用</span>
        </>
      );
    }

    const auth_rights = userRights.filter((item) =>
      AUTH_RIGHT_PACKAGE_CODES.includes(item.right_package_code)
    );

    if (isCompanyVipByUser(user)) {
      if (auth_rights.length) {
        return "企业高级授权";
      }
      return "企业标准授权";
    } else if (isBigCompanyByUser(user)) {
      return "大企业定制授权";
    } else {
      return "个人标准授权";
    }
  });

  const { authText, noAuth, isBigCompany } = useMemo(() => {
    if (isGdMerged(user)) {
      const version = MATERIAL_VIP_VERSION[gdVip[0].solution_version];

      const isPlus = isPlusByGdVip(gdVip);

      return {
        authText: isPlus ? version.plusAuthText : version.authText,
        isBigCompany: version.authType === AUTH_TYPE.BIG_COMPANY,
        noAuth: version.authType === AUTH_TYPE.NOAUTH,
      };
    } else {
      const noAuth = isNoAuthByUser(user);
      return {
        authText: getAuthText(noAuth),
        isBigCompany: isBigCompanyByUser(user),
        noAuth,
      };
    }
  }, [gdVip, getAuthText, user]);

  const { hasAllDownload, downloadUsage, disabled } = useMemo(() => {
    const infos = preDownloadInfo.vip_cert_subjects;
    if (!infos || Object.values(infos).length === 0) {
      return {
        hasAllDownload: !!preDownloadInfo.is_download,
        downloadUsage: subjects?.length || 1,
      };
    }

    const values = Object.values(infos);

    const hasAllDownload = values.every((item) => item.download);
    const downloadUsage = values.filter((item) => !item.download).length;

    const download = rightData?.inspect;

    const disabled =
      // 存在无效授权主体
      values.some((item) => !item.valid) ||
      // 剩余下载次数不足  is_finite_quantity: 限定下载数量
      (download?.is_finite_quantity &&
        Number(download.left) - downloadUsage < 0);

    return {
      /** 每个授权主体是否都下载过 */
      hasAllDownload,
      /** 此次下载使用量 */
      downloadUsage,
      /** 是否禁用下载按钮 */
      disabled,
    };
  }, [preDownloadInfo, subjects?.length]);

  if (!rightData) {
    // 异常账号数据，没有下载权益包
    if (isNewVip) {
      onIncreaseDownloadCount();
    } else {
      HBMessage.error("没有下载权益，请联系客服");
    }

    return null;
  }

  // 下载次数不够
  const isDownloadCountNotEnough =
    Number(rightData?.inspect?.left) < downloadUsage;

  return (
    <HBModal
      className={classNames(styles.modal, "black-theme")}
      title={editOnline ? "素材编辑" : "素材下载"}
      width={480}
      footer={null}
      {...props}
    >
      <div
        className={styles.container}
        data-dialog-title={editOnline ? "素材编辑" : "素材下载"}
        data-gd-expose="dialog_expose"
        data-subject-name="弹窗"
        data-module-name="版权素材保存到弹窗"
      >
        <div className={styles.info}>
          <div className={styles.img}>
            <Image src={imageUrl(pin.file)} alt="" width={64} height={64} />
          </div>
          <div className={styles.infoContent}>
            <div className={styles.title} title={materialInfo?.title}>
              {format} 文件 {materialType && ` · ${materialType}`}
            </div>
            <div className={styles.desc}>
              {hasAllDownload ? (
                <>
                  消耗&nbsp;<span className={styles.num}>0</span>&nbsp;次 (
                  {noAuth ? "下载过" : "授权过"}
                  的素材，会员期内可以免费重复下载)
                </>
              ) : (
                <>
                  消耗 <span className={styles.num}>{downloadUsage}</span> 次
                  &nbsp;&nbsp;
                  {isDownloadCountNotEnough ? (
                    <span className={styles.warn}>剩余下载次数不足</span>
                  ) : (
                    `(${!isBigCompany && !isNewVip ? "今日" : ""}剩余 ${
                      rightData?.inspect?.left
                    } 次)`
                  )}
                </>
              )}
            </div>
          </div>
          {isSaveToBoard ? (
            <Button
              className={styles.saveToSpaceButton}
              type="primary"
              onClick={saveToSpaceOnly}
              disabled={disabled}
              loading={downloadLoading}
              data-gd-click="button_click"
              data-button-name={"确认保存"}
            >
              保存到创作空间
            </Button>
          ) : isDownloadCountNotEnough ? (
            <Button
              className={styles.button}
              type="primary"
              onClick={onIncreaseDownloadCount}
              data-gd-click="button_click"
              data-button-name={"增加下载次数"}
            >
              增加下载次数
            </Button>
          ) : (
            <Button
              className={styles.button}
              type="primary"
              onClick={onDownloadClick}
              disabled={disabled}
              loading={downloadLoading}
              data-gd-click="button_click"
              data-button-name={
                editOnline
                  ? "立即编辑"
                  : hasAllDownload
                  ? "再次下载"
                  : "确定下载"
              }
            >
              {editOnline
                ? "立即编辑"
                : hasAllDownload
                ? "再次下载"
                : "确定下载"}
            </Button>
          )}
        </div>

        <div className={styles.divider} />

        <div className={styles.body}>
          {subjects?.length > 1 && (
            <div className={styles.item}>
              <div className={styles.label}>授权主体</div>
              <div className={styles.value}>
                <SubjectSelect
                  value={subjectValue}
                  options={subjectSelectOption}
                  onChange={(value) => {
                    setSubjectValue(value);
                    if (value?.length > 0) {
                      preDownload(value);
                    } else {
                      HBMessage.error("请先选择授权主体");
                    }
                  }}
                ></SubjectSelect>

                <a
                  href={VIP_CERT_PAGE}
                  target="_blank"
                  style={{ flexShrink: 0 }}
                >
                  管理
                </a>
              </div>
            </div>
          )}

          <div className={styles.row}>
            <div className={styles.item}>
              <div className={styles.label}>授权类型</div>
              <div className={styles.value}>{authText}</div>
            </div>
            <Checkbox
              className={classNames(styles.checkBox, styles.blackTheme)}
              checked={saveToSpace}
              onChange={(e) => setSaveToSpace(e.target.checked)}
            >
              下载后保存到创作空间
            </Checkbox>
          </div>
        </div>
        {vipLeftDay < 7 && (
          <div className={styles.alert}>
            <span className={styles.text}>
              <HBIcon type="ic_info" />
              素材下载会员将在
              {getTimeDuration({
                time: lastVip.expires_at as number,
                afterText: "",
                reverse: true,
              })}
              后到期
            </span>
            <a onClick={() => openBuyVipDialog("续费_素材下载弹窗")}>去续费</a>
          </div>
        )}
      </div>
      {isPersonalOrg(orgInfo?.org) && (
        <div
          className={styles.footer}
          onClick={(e) => {
            props.onCancel && props.onCancel(e);
            OrgService.getInstance().upgradeOrg();
          }}
        >
          <Image
            src="https://grocery-cdn.huaban.com/file/usericon.svg"
            width={20}
            height={20}
          />
          <span className={styles.text}>升级为团队，素材共享更省心</span>
          <HBIcon className={styles.icon} type="ic_chevron_right" />
        </div>
      )}
    </HBModal>
  );
}
const MaterialDownloadDialog = ModalWrap(MaterialDownloadInfoModal);
export default MaterialDownloadDialog;
